import React, { useState, useEffect } from "react";
import useTranslation from "next-translate/useTranslation";
import { useFela } from "react-fela";
import { useRouter } from "next/router";

import { Grid } from "@/components/Grid";
import { Pricing, IPricing } from "@/containers/Pricing";
import { PricingProvider } from "@/containers/Pricing/state/context";
import { useTheme } from "@/hooks/useTheme";
import { Heading } from "@/components/Heading";
import { PricingSlider } from "@/containers/PricingSlider";
import { useInquiryForm } from "@/hooks/useInquiryForm";
import { FormSourceType } from "@/containers/InquiryForm";
import { useSearchParams } from "@/hooks/useSearchParams";
import { PricingTab } from "./PricingTab";

type IProps = IPricing & JSX.IntrinsicElements["div"];

export const PricingSectionWithTabs: React.FC<IProps> = props => {
  const { colors, bp } = useTheme();
  const { css } = useFela();
  const { t } = useTranslation("section/pricing");
  const { toggleInquiryForm } = useInquiryForm();
  const [hasInquiryFormBeenOpened, setHasInquiryFormBeenOpened] = useState(
    false,
  );
  const params = useSearchParams();
  const router = useRouter();

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const defaultActiveTab = router?.asPath?.includes("#backup") ? 1 : 0;
    setActiveTab(defaultActiveTab);
  }, [router]);

  useEffect(() => {
    if (params.get("contact") === "backup" && !hasInquiryFormBeenOpened) {
      setHasInquiryFormBeenOpened(true);
      toggleInquiryForm(FormSourceType.BACKUP_MORE_INQUIRY);
    }
  }, [params, hasInquiryFormBeenOpened, toggleInquiryForm]);

  return (
    <>
      <Grid
        customStyle={{
          flexDirection: "column",
          paddingBottom: 0,
          [bp.FROM_TABLET]: {
            paddingBottom: 0,
          },
          [bp.FROM_DESKTOP]: {
            paddingBottom: 0,
          },
        }}
        wrapperStyle={{
          position: "relative",
          background: `linear-gradient(89.82deg, ${colors.GRADIENT_BLUE_DARK} 0%, ${colors.GRADIENT_PURPLE} 100%)`,
        }}
        id="PricingSectionHeader"
      >
        <div className={css({})}>
          <div className={css({ marginBottom: 64, textAlign: "center" })}>
            <Heading
              as="h1"
              customStyle={{ marginBottom: 24, color: colors.WHITE }}
            >
              {t("title")}
            </Heading>
            <Heading
              as="h4"
              customStyle={{
                color: colors.WHITE,
                fontWeight: 400,
                letterSpacing: "0.5px",
              }}
            >
              {t("subtitle")}
            </Heading>
          </div>
          <div className={css({ display: "flex", justifyContent: "center" })}>
            <PricingTab
              isActive={activeTab === 0}
              title={t("tabs.converterTabTitle")}
              subtitle={t("tabs.converterTabSubtitle")}
              icon="SWITCH-ICON"
              onClick={() => setActiveTab(0)}
            />
            <PricingTab
              isActive={activeTab === 1}
              title={t("tabs.backupToolTabTitle")}
              subtitle={t("tabs.backupToolTabSubtitle")}
              icon="CLOCK"
              onClick={() => setActiveTab(1)}
            />
          </div>
        </div>
      </Grid>
      <Grid
        customStyle={{
          flexDirection: "column",
          padding: 0,
        }}
        wrapperStyle={{ position: "relative" }}
        id="PricingSection"
      >
        <PricingProvider>
          {activeTab === 0 ? (
            <Pricing {...props} />
          ) : (
            <PricingSlider {...props} />
          )}
        </PricingProvider>
      </Grid>
    </>
  );
};
